import { Component, OnInit, ViewChild } from '@angular/core';
import { MatSort, Sort } from '@angular/material/sort';
import { ActivatedRoute, Router } from '@angular/router';
import { BaseComponent } from '@shared/base/base.component';
import { BaseDatasource } from '@shared/base/base.datasource';
import { TableColumn } from '@shared/interface/table.interface';
import { BaseTableService } from '@shared/base/base-table-service.service';
import { UrlUtilsService } from '@utils/url-utils.service';
import { API } from '@config/constants/api.constant';
import { BehaviorSubject, Observable } from 'rxjs';
import { STRING_CONSTANTS } from '@config/constants/string.constants';
import { FilterService } from '@services/filter.service';
import { IProgramMarketingList, IDownloadCsv } from '../../retailers.interface';
import { ModalProgressScanRetailerComponent } from '@shared/components/modal/modal-progress-scan-retailer/modal-progress-scan-retailer.component';
import { IModalProgressScanMpr } from '@pages/sales-marketing/program-marketing/program-marketing-retailer/program-marketing-retailer.interface';
import { RetailerService } from '../../retailer.service';
import { UtilitiesService } from '@services/utilities.service';

@Component({
  selector: 'app-program-marketing-list',
  templateUrl: './program-marketing-list.component.html',
  styleUrls: ['./program-marketing-list.component.scss'],
})
export class ProgramMarketingListComponent extends BaseComponent implements OnInit {
  @ViewChild(MatSort, { static: false }) matSort: MatSort;
  @ViewChild('modalDetail') private modalDetail: ModalProgressScanRetailerComponent;

  string_filter: string = '';

  // table
  programList: IProgramMarketingList[];
  tableColumns: TableColumn[];
  displayedColumns: string[];
  baseDatasource: BaseDatasource<IProgramMarketingList>;
  finishLoading$: Observable<boolean> = new BehaviorSubject(false);
  totalCount: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  privilegeDetail: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  modalScanMpr: BehaviorSubject<IModalProgressScanMpr> = new BehaviorSubject<IModalProgressScanMpr>({} as IModalProgressScanMpr);
  dataCSV: BehaviorSubject<IDownloadCsv[]> = new BehaviorSubject<IDownloadCsv[]>([]);
  titleCSV: BehaviorSubject<string> = new BehaviorSubject<string>('');
  downloaded: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  is_multilevel: boolean = true;
  STRING_CONSTANTS = STRING_CONSTANTS;
  header: boolean = true;
  idModal: string = '';

  constructor(
    private router: Router,
    private activeRoute: ActivatedRoute,
    private baseTableService: BaseTableService<IProgramMarketingList>,
    private urlParamService: UrlUtilsService,
    private filterService: FilterService,
    private retailerService: RetailerService,
    private utils: UtilitiesService
  ) {
    super();
  }

  @ViewChild('fileImportInput') fileImportInput: any;

  ngOnInit(): void {
    this.baseTableService.responseDatabase.subscribe((response) => (this.baseDatasource = response));
    this.queryHandler();
    this.finishLoading$ = this.baseDatasource.finishLoading;
    this.initPageInfo();
    this.setTableData();
  }

  initPageInfo() {
    this.baseDatasource.tableSubject.subscribe((val) => {
      this.programList = val;
    });
  }

  setTableData() {
    this.tableColumns = [
      {
        key: 'program_name',
        title: 'Program Marketing',
        isSortable: false,
      },
      {
        key: 'qr_scan_quantity',
        title: 'Jumlah Scan QR',
        isSortable: false,
      },
      {
        key: 'string_level',
        title: 'Capaian Tingkatan',
        isSortable: false,
      },
      {
        key: 'progress.completion',
        title: 'Progres Penyelesaian',
        isSortable: true,
      },
      {
        key: 'status_string',
        title: 'Status',
        isSortable: false,
      },
      {
        key: 'actions',
        title: 'Action',
      },
    ];
    this.displayedColumns = this.tableColumns.map((item) => item.key);
  }

  sortTable(param: Sort) {
    const sortby = this.tableColumns.find((column) => column.key === param.active);
    param.active = sortby?.key ?? '';
    this.sortDataSource(param);
  }

  isEmptyData = () => !this.string_filter && this.baseDatasource.isTableLoaded && this.baseDatasource.totalItem$.getValue() === 0;

  searchNotFound = () => this.baseDatasource.isTableLoaded && this.baseDatasource.totalItem$.getValue() === 0 && !!this.string_filter;

  getNestedValue(obj: any, path: string): any {
    return path.split('.').reduce((acc, part) => acc && acc[part], obj);
  }

  sortDataSource(sortParams: Sort) {
    const keyName = sortParams.active;

    this.programList = this.programList.sort((a, b) => {
      let aValue: any = this.getNestedValue(a, keyName);
      let bValue: any = this.getNestedValue(b, keyName);

      const parsePercent = (val: any): number => {
        if (typeof val === 'string' && val.trim().endsWith('%')) {
          return parseFloat(val.replace('%', '').trim());
        }
        return parseFloat(val) || 0;
      };

      aValue = parsePercent(aValue);
      bValue = parsePercent(bValue);

      if (sortParams.direction === 'asc') {
        return aValue - bValue;
      } else if (sortParams.direction === 'desc') {
        return bValue - aValue;
      }
      return 0;
    });

    this.baseDatasource.tableSubject.next(this.programList);
  }

  changePageEvent($event: BaseDatasource<any>) {
    this.filterService.changePageEvent($event, this.string_filter ?? '');
  }

  handleAction(val: any, multi: boolean) {
    const { id } = this.activeRoute.snapshot.params;
    this.retailerService.getInitialDetailProgressScanRetailer(val, id).subscribe((value) => {
      if (!value) return;

      const { data } = value;
      const detail = data;
      const scan: IModalProgressScanMpr = { detail };

      if (!!scan) {
        this.modalScanMpr.next(scan);
        this.titleCSV.next(scan.detail.name);
        this.is_multilevel = multi;
      }
      this.idModal = val;
    });
    return this.modalDetail.openModal();
  }

  getDataScan(programId: string) {
    const { id } = this.activeRoute.snapshot.params;
    this.downloaded.next(true);
    this.retailerService.getDownloadCSV(programId, id).subscribe((value) => {
      if (!value) return;
      const { data } = value;
      this.dataCSV.next(data);
      this.exportToCSV();
    });
  }

  handleDownloadProgressScan() {
    this.getDataScan(this.idModal);
  }

  exportToCSV() {
    this.utils.downloadFile<IDownloadCsv>(this.dataCSV.value, this.titleCSV.value, Object.keys(this.dataCSV.value[0]));
    return this.downloaded.next(false);
  }

  getDetailModalScan() {
    return this.modalScanMpr;
  }

  queryHandler() {
    const { id } = this.activeRoute.snapshot.params;
    this.activeRoute.queryParams.subscribe((data) => {
      this.string_filter = data.string_filter;
      const param = this.urlParamService.sliceQueryParams();
      if (param) {
        this.baseTableService.loadDataTable(API.PROGRAM_MARKETING_RETAILER.RETAILER_PROGRAM_LIST + id, param);
      } else {
        this.baseTableService.loadDataTable(API.PROGRAM_MARKETING_RETAILER.RETAILER_PROGRAM_LIST + id, '');
      }
    });
  }

  handleSearch(event: string) {
    const snapshot = this.activeRoute.snapshot;
    const dataParam = { ...snapshot.queryParams };
    if (event) {
      delete dataParam.page;
      this.router
        .navigate([], {
          queryParams: {
            ...dataParam,
            string_filter: '' || event,
          },
        })
        .then(() => null);
    } else {
      delete dataParam.string_filter;
      this.router.navigate([], { queryParams: dataParam }).then(() => null);
      this.string_filter = '';
    }
  }

  displayProgramName(value: IProgramMarketingList | any, completion: boolean): any {
    return completion ? value?.progress?.completed_times ?? '' : value?.program_name ?? '';
  }

  displayQRScan(value: IProgramMarketingList | any, target: boolean): any {
    return target ? value?.qr_scan_quantity?.target ?? '' : value?.qr_scan_quantity?.value ?? '';
  }

  displayProgress(value: IProgramMarketingList | any, completion: boolean) {
    return completion ? value?.progress?.completion ?? '' : value?.is_multilevel_target === true ? value?.progress?.string_level : '';
  }
}
